import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { MediaTile } from "@/components/MediaTile";
import { MediaLightbox } from "@/components/MediaLightbox";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Inbox, X, Sparkles } from "lucide-react";
import { toast } from "sonner";

const RateLater = () => {
  const { user, loading: authLoading, login } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get("/me/rate-later"); setItems(r.data); } catch { setItems([]); }
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  const remove = async (id, e) => {
    e?.stopPropagation();
    setItems((p) => p.filter((x) => x.id !== id));
    try { await api.delete(`/me/rate-later/${id}`); toast.success("Removed from inbox"); } catch {}
  };

  if (authLoading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;
  if (!user) {
    return (
      <div className="min-h-screen bg-background"><Navbar />
        <div className="max-w-md mx-auto text-center py-24 px-6">
          <Inbox className="w-10 h-10 text-primary mx-auto mb-4" />
          <h1 className="font-heading text-2xl mb-2">Your rate-later inbox</h1>
          <p className="text-muted-foreground mb-6 text-sm">Sign in to see the photos and videos you saved while swiping.</p>
          <button onClick={login} className="rounded-full bg-primary text-primary-foreground px-6 py-2.5 font-medium">Sign in</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-1">
          <Inbox className="w-7 h-7 text-primary" />
          <h1 className="font-heading text-3xl">To Rate</h1>
        </div>
        <p className="text-muted-foreground mb-6 text-sm">Everything you swiped right to save. Tap one to rate it — it clears from here once you do.</p>

        {!items ? <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>
          : items.length === 0 ? (
            <div className="border border-dashed border-border rounded-xl py-16 text-center">
              <Sparkles className="w-8 h-8 text-primary mx-auto mb-3" />
              <p className="text-muted-foreground">Nothing saved yet.</p>
              <button data-testid="ratelater-go-swipe" onClick={() => navigate("/swipe")} className="mt-4 rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-medium">Start swiping</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="ratelater-grid">
              {items.map((item, i) => (
                <div key={item.id} className="relative">
                  <MediaTile item={item} index={i} onOpen={(x) => setOpenId(x.id)} />
                  <button data-testid={`ratelater-remove-${item.id}`} onClick={(e) => remove(item.id, e)}
                    className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-black/70 backdrop-blur flex items-center justify-center text-white hover:bg-destructive transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
      </div>
      <MediaLightbox mediaId={openId} open={!!openId} onClose={() => { setOpenId(null); load(); }} onChanged={load} onNavigate={setOpenId} playlist={items || []} />
    </div>
  );
};

export default RateLater;
